import { useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  Flex,
  Heading,
  HStack,
  IconButton,
  InputGroup,
  Link,
  SimpleGrid,
  Skeleton,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Tag,
  TagLeftIcon,
  TagLabel,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Link as ReactRouterLink,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { z } from 'zod';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
  finleyAssertUnreachable,
  consts as commonConsts,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';
import { commonFormulas } from '@finley-technologies/formula-engine';
import { progress } from '@finley-technologies/tape-builder';

import {
  DeliverableArtifactConfigGQLType,
  DeliverableArtifactGQLType,
  DeliverableArtifactInstanceGQLType,
  Maybe,
  MonthlyStatementConfigGQLType,
  RequiredDataTapeGQLType,
  RequiredXlsxTapeGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  calculateDetailedStatus,
  DetailedStatusBadge,
  UploadProgressBar,
} from '../Status';
import { getDeliverableFinalOutputReceiptsViewUrl } from '../utils';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { DownloadButton } from '~/core/routes/deliverables/[deliverableId]/DownloadActionButton';
import {
  GenerateOutputsModal,
  getMostRecentActiveInputDependencyIds,
} from '~/core/routes/deliverables/[deliverableId]/GenerateOutputs';
import { useCreateDeliverableArtifactInstanceMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/CreateDeliverableArtifactInstance.gql.generated';
import { useCreateDeliverableContainerReceiptMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/CreateDeliverableContainerReceipt.gql.generated';
import { useRefreshDeliverableMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/RefreshDeliverable.gql.generated';
import { useSubmitMonthlyStatementDeliverableMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/SubmitMonthlyStatementDeliverable.gql.generated';
import { useUpdateFailedDeliverableArtifactInstanceMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/UpdateFailedDeliverableArtifactInstance.gql.generated';
import { useGetBorrowingBasePipelineArtifactInstancesErrorsQuery } from '~/core/routes/deliverables/[deliverableId]/queries/GetBorrowingBasePipelineArtifactInstancesErrors.gql.generated';
import {
  GetDeliverableArtifactsByContainerIdDocumentGQLType,
  useGetDeliverableArtifactsByContainerIdQuery,
} from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableArtifactsByContainerId.gql.generated';
import {
  GetDeliverableContainerByIdDocumentGQLType,
  useGetDeliverableContainerByIdQuery,
} from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableContainerById.gql.generated';
import { useGetDeliverableContainerReceiptsQuery } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableContainerReceipts.gql.generated';
import { Upload } from '~/core/routes/deliverables/[deliverableId]/Upload';
import { SingleEntityDrawer as DeliverableEventLogSingleEntityDrawer } from '~/core/routes/deliverables/EntityEventLogDrawers';
import { useGetThreadsByDomainIdentifierQuery } from '~/packages/comments/queries/GetThreadsByDomainIdentifier.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Select } from '~/packages/common-frontend/components/Select';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useSearchParamsDisclosure } from '~/packages/common-frontend/hooks/useSearchParamDisclosure';
import { ColorScheme } from '~/packages/common-frontend/theme';
import {
  getUnexpectedErrorMessage,
  getHumanReadableDateRange,
} from '~/packages/common-frontend/utils';
import { EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM } from '~/packages/event-logs/constants';
import { useGetRequiredDataTapesQuery } from '~/packages/manualDataTapeUploads/requests/requests.gql.generated';
import {
  DataTape,
  DataTapeState,
  DataTapeUiStatus,
} from '~/packages/manualDataTapeUploads/types';
import { directUpload } from '~/packages/manualDataTapeUploads/ui/DataTapeContainer';
import { DataTapeErrorView } from '~/packages/manualDataTapeUploads/ui/DataTapeErrorView';
import {
  AdjustCurrentLiabilitiesButton,
  MonthlyStatementDailyAggregateTable,
  MonthlyStatementSummaryBalance,
} from '~/packages/monthlyStatement';
import { useGetStatementQuery } from '~/packages/reporting/requests/reporting.gql.generated';

// Types
const RouteParamsStruct = z.object({
  deliverableId: z.string({ message: 'Missing URL parameter' }),
});
// Used in progress bar to track upload progression.
type DeliverableArtifactToDataTapeMap = {
  [key: string]: // keyed by deliverable artifact id
  | {
        loadingPercent: number;
        status: DataTapeUiStatus;
        bbplArtifactSlug: string;
      }
    | undefined;
};

type ArtifactInstanceStatus =
  | 'submitted'
  | 'processing'
  | 'notYetSubmitted'
  | 'expiredVersion';

type ArtifactStatus = 'submitted' | 'processing' | 'notYetSubmitted';

// Constants
const artifactStatusColor: {
  [key in ArtifactInstanceStatus]: ColorScheme;
} = {
  notYetSubmitted: 'orange',
  processing: 'orange',
  submitted: 'green',
  expiredVersion: 'gray',
};

const artifactStatusIcon: { [key in ArtifactInstanceStatus]: string } = {
  notYetSubmitted: 'mailbox',
  processing: 'spinner-third',
  submitted: 'mailbox-flag-up',
  expiredVersion: 'calendar-circle-exclamation',
};

// Helpers
const calculateArtifactInstanceStatus = (
  artifactInstance: Maybe<DeliverableArtifactInstanceGQLType>,
): ArtifactInstanceStatus => {
  if (artifactInstance && !artifactInstance.active) {
    return 'expiredVersion';
  }
  if (artifactInstance && artifactInstance.active) {
    if (artifactInstance.status === 'success') {
      return 'submitted';
    }
    if (artifactInstance.status === 'inProgress') {
      return 'processing';
    }
    // if artifactInstance.status === 'error' then artifact status should still be 'notYetSubmitted'
  }

  return 'notYetSubmitted';
};

const calculateArtifactStatus = (
  artifact: DeliverableArtifactGQLType,
): ArtifactStatus => {
  const { instances } = artifact;

  const activeInstance = instances.find((i) => i && i.active);

  const artifactInstanceStatus = calculateArtifactInstanceStatus(
    activeInstance,
  );

  return artifactInstanceStatus === 'expiredVersion'
    ? 'notYetSubmitted'
    : artifactInstanceStatus;
};

const getDataTapeState = ({
  tape,
  inProgressSlugs,
  completedSlugs,
}: {
  tape: RequiredDataTapeGQLType | RequiredXlsxTapeGQLType;
  inProgressSlugs: string[];
  completedSlugs: string[];
}): DataTapeState => {
  const progressSummary: Util.Maybe<progress.ProgressSummary> =
    tape.progressSummary;
  let localStatus: DataTapeUiStatus;

  // calculate status
  if (
    inProgressSlugs.includes(tape.artifactSlug) ||
    progressSummary?.status === 'inProgress'
  ) {
    // TODO(jon): differentiate between `uploading` and `processing`.
    localStatus = 'uploading';
  } else if (progressSummary?.status === 'error') {
    localStatus = 'error';
  } else if (completedSlugs.includes(tape.artifactSlug)) {
    localStatus = 'success';
  } else {
    localStatus = 'none';
  }

  finleyAssert(tape.__typename, 'Missing GQL Typename');
  finleyAssert(
    Util.isSupportedFileExtension(tape.fileExtension),
    `Invalid file extension: ${tape.fileExtension}`,
  );

  switch (tape.__typename) {
    case 'RequiredDataTape':
      return {
        ...tape,
        fileType: tape.fileExtension,
        key: tape.artifactSlug,
        localStatus,
        progressSummary: tape.progressSummary ?? null,
      };
    case 'RequiredXlsxTape':
      return {
        ...tape,
        fileType: tape.fileExtension,
        key: tape.artifactSlug,
        localStatus,
        progressSummary: tape.progressSummary ?? null,
      };
    default:
      return finleyAssertUnreachable(tape.__typename);
  }
};

// Fragments
const ArtifactStatusBadge = ({
  status,
}: {
  status: ArtifactInstanceStatus;
}) => {
  const colorScheme = artifactStatusColor[status];

  return (
    <Tag colorScheme={colorScheme} variant="solid-muted" flexShrink={0}>
      <TagLeftIcon>
        <Icon icon={artifactStatusIcon[status]} />
      </TagLeftIcon>
      <TagLabel>{commonUtils.toTitleCase(status)}</TagLabel>
    </Tag>
  );
};

const ArtifactStatusCell = ({ status }: { status: ArtifactStatus }) => {
  return (
    <Tag
      colorScheme={artifactStatusColor[status]}
      variant="solid-muted"
      flexShrink={0}
      size="sm"
    >
      <TagLeftIcon>
        <Icon icon={artifactStatusIcon[status]} />
      </TagLeftIcon>
      <TagLabel>{commonUtils.toSentenceCase(status)}</TagLabel>
    </Tag>
  );
};

const ArtifactInstanceStatusBadge = ({
  status,
}: {
  status: ArtifactInstanceStatus;
}) => {
  switch (status) {
    case 'notYetSubmitted':
    case 'submitted':
      return <ArtifactStatusBadge status={status} />;
    case 'processing':
      return <ArtifactStatusBadge status="notYetSubmitted" />;
    case 'expiredVersion':
      return <ArtifactStatusBadge status="expiredVersion" />;
    default:
      return finleyAssertUnreachable(status);
  }
};

type UploadButtonProps = {
  creditFacilityName: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  fontAwesomeIcon: string;
  buttonText: string;
  iconOnly?: boolean;
  dataTape?: DataTapeState;
  onClose?: () => void;
  /** If true, default to new version; otherwise default to highest available existing version */
  defaultToNewVersion: boolean;
  onSubmitDataTape: (
    file: File,
    tape: DataTape,
    uploadReportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => Promise<void>;
};
const UploadButton = ({
  creditFacilityName,
  creditFacilityId,
  organizationId,
  artifact,
  fontAwesomeIcon,
  buttonText,
  iconOnly,
  dataTape,
  onSubmitDataTape,
  onClose = () => {},
  defaultToNewVersion,
}: UploadButtonProps) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  return (
    <>
      {iconOnly ? (
        <IconButton
          aria-label="Upload file"
          size="sm"
          icon={<Icon icon={fontAwesomeIcon} color="blue.400" />}
          variant="ghost"
          onClick={() => {
            setIsUploadModalOpen(true);
          }}
        />
      ) : (
        <Button
          color="blue.400"
          leftIcon={
            <Icon icon={fontAwesomeIcon} color="blue.400" boxSize={4} />
          }
          variant="ghost"
          fontSize="sm"
          py="2px"
          px="8px"
          onClick={() => {
            setIsUploadModalOpen(true);
          }}
        >
          {buttonText}
        </Button>
      )}
      <Upload
        creditFacilityName={creditFacilityName}
        creditFacilityId={creditFacilityId}
        organizationId={organizationId}
        artifact={artifact}
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          onClose();
        }}
        dataTape={dataTape}
        onSubmitDataTape={onSubmitDataTape}
        defaultToNewVersion={defaultToNewVersion}
      />
    </>
  );
};

type DrawerViewProps = {
  creditFacilityName: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  dataTape?: DataTapeState;
  onSubmitDataTape: (params: {
    file: File;
    tape: DataTape;
    deliverableArtifactId: string;
    uploadReportingDate: Util.FullDateStr;
    fileVersion: number;
  }) => Promise<void>;
  // errors should only exist for BBPL types (loan tape, repayment tape, supplemental tape)
  // if errors is undefined, there are no errors for this artifact
  // if errors exist but errors.errors is an empty array, that means there was an error but we do not have additional info
  // if errors.errors isn't empty, then we show the data tape errors in the field below
  errors?: {
    errors: progress.AggregatedError[];
    deliverableArtifactInstanceId: string;
  };
};

const DefaultDrawerBody = ({
  creditFacilityName,
  creditFacilityId,
  organizationId,
  artifact,
  onClose,
  dataTape,
  onSubmitDataTape,
  errors,
}: Omit<DrawerViewProps, 'isOpen'>) => {
  const toast = useToast();

  const [
    updateFailedDeliverableArtifactInstanceMutation,
  ] = useUpdateFailedDeliverableArtifactInstanceMutation({
    onCompleted: () => {
      toast({
        title: 'Marked deliverable artifact file version as error.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title:
          'Unable to mark deliverable artifact file version as error. Please contact Finley support.',
        status: 'error',
        position: 'top',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  const status = calculateArtifactStatus(artifact);
  const nonErrorInstances = errors
    ? artifact.instances.filter(
        (i) => i.id !== errors.deliverableArtifactInstanceId,
      )
    : artifact.instances;
  return (
    <>
      <Stack spacing="20px" mb="20px">
        <HStack>
          <Text as="b" width="20%" fontSize="sm">
            Status
          </Text>

          {errors ? (
            <Tag
              colorScheme="red"
              variant="solid-muted"
              flexShrink={0}
              size="sm"
            >
              <TagLeftIcon>
                <Icon icon="circle-x" />
              </TagLeftIcon>
              <TagLabel>Upload Error</TagLabel>
            </Tag>
          ) : (
            <ArtifactStatusBadge status={status} />
          )}
        </HStack>

        {/* Show errors here if they exist */}
        {!!errors && errors.errors.length > 0 && (
          <DataTapeErrorView errors={errors.errors} numOfRows={8} />
        )}

        {(nonErrorInstances.length === 0 || errors) && (
          <HStack>
            <Text as="b" width="20%" fontSize="sm">
              Action
            </Text>
            <UploadButton
              creditFacilityId={creditFacilityId}
              creditFacilityName={creditFacilityName}
              organizationId={organizationId}
              artifact={artifact}
              fontAwesomeIcon="arrow-up-from-bracket"
              buttonText="Upload"
              defaultToNewVersion={false}
              dataTape={dataTape}
              onSubmitDataTape={(
                file,
                tape,
                uploadReportingDate,
                fileVersion,
              ) =>
                onSubmitDataTape({
                  file,
                  tape,
                  deliverableArtifactId: artifact.id,
                  uploadReportingDate,
                  fileVersion,
                })
              }
              onClose={onClose}
            />
          </HStack>
        )}

        {nonErrorInstances.length > 0 && (
          <HStack alignItems="flex-start">
            <Text as="b" width="20%" fontSize="sm" mt={2}>
              Files
            </Text>
            <Stack spacing="0px">
              {nonErrorInstances.map((instance) => {
                return (
                  <SimpleGrid
                    key={instance.id}
                    columns={3}
                    spacing="10px"
                    alignItems="center"
                  >
                    <Text
                      color="gray.600"
                      fontSize="sm"
                      pr={4}
                    >{`Version ${instance.version}`}</Text>
                    {instance.status === 'success' && (
                      <DownloadButton artifactInstanceId={instance.id} />
                    )}
                    {instance.status === 'error' && (
                      <Text
                        color="red.400"
                        fontSize="sm"
                        fontWeight="semibold"
                        pl={4}
                      >
                        File errored.
                      </Text>
                    )}
                    {instance.active && instance.status === 'inProgress' && (
                      <Tooltip
                        label="Cancel file processing and mark as error."
                        placement="top"
                      >
                        <Button
                          colorScheme="red"
                          color="red.400"
                          leftIcon={
                            <Icon
                              icon="circle-xmark"
                              color="red.400"
                              boxSize={4}
                            />
                          }
                          variant="ghost"
                          fontSize="sm"
                          py="2px"
                          px="8px"
                          onClick={async () => {
                            // update deliverable artifact instance from 'inProgress' -> 'error'
                            await updateFailedDeliverableArtifactInstanceMutation(
                              {
                                variables: {
                                  instanceId: instance.id,
                                },
                              },
                            );
                            onClose();
                          }}
                        >
                          Cancel
                        </Button>
                      </Tooltip>
                    )}
                    {instance.active && instance.status !== 'inProgress' ? (
                      <UploadButton
                        creditFacilityId={creditFacilityId}
                        creditFacilityName={creditFacilityName}
                        organizationId={organizationId}
                        artifact={artifact}
                        fontAwesomeIcon="arrows-rotate"
                        buttonText="Overwrite"
                        defaultToNewVersion
                        dataTape={dataTape}
                        onSubmitDataTape={(
                          file,
                          tape,
                          uploadReportingDate,
                          fileVersion,
                        ) =>
                          onSubmitDataTape({
                            file,
                            tape,
                            deliverableArtifactId: artifact.id,
                            uploadReportingDate,
                            fileVersion,
                          })
                        }
                        onClose={onClose}
                      />
                    ) : (
                      <Box />
                    )}
                  </SimpleGrid>
                );
              })}
            </Stack>
          </HStack>
        )}
      </Stack>
    </>
  );
};

const MonthlyStatementSubmittedDrawerBody = ({
  artifactInstance,
}: {
  artifactInstance: DeliverableArtifactInstanceGQLType;
}) => {
  finleyAssert(
    artifactInstance.status === 'success',
    'Expected monthly statement to be submitted',
  );

  const { completedObject } = artifactInstance;

  finleyAssert(
    completedObject,
    'Expected a completed monthly statement data for artifact',
  );

  const dailyAggregateData = completedObject.object;

  return (
    <>
      <VStack
        backgroundColor={colors.white}
        w="100%"
        p="24px"
        borderRadius="8px"
        border={`1px solid ${colors.gray[100]}`}
      >
        <MonthlyStatementSummaryBalance
          totalBalance={dailyAggregateData.summary.totalBalance}
          balances={dailyAggregateData.summary.balances}
        />
      </VStack>

      <Flex w="100%" alignItems="center" justify="space-between">
        <Heading fontSize="20px">Daily Totals</Heading>
        {/* MSR-TODO hook up daily aggregation download */}
      </Flex>

      <MonthlyStatementDailyAggregateTable
        dailyAggregateData={dailyAggregateData}
      />
    </>
  );
};

const MonthlyStatementInProgressDrawerBody = ({
  artifactInstanceStatus,
  artifactContainerId,
  artifactId,
  creditFacilityId,
  startDate,
  endDate,
  onClose,
  artifactInstanceId,
  setIsTableLoading,
}: {
  artifactInstanceStatus: ArtifactInstanceStatus;
  creditFacilityId: string;
  artifactContainerId: string;
  artifactId: string;
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
  onClose: () => void;
  artifactInstanceId?: string;
  setIsTableLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();

  const [
    submitMonthlyStatement,
    { loading: isSubmitLoading },
  ] = useSubmitMonthlyStatementDeliverableMutation({
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      toast({
        title: 'Marked deliverable artifact file version as completed.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
      onClose();
    },
    onError: () => {
      toast({
        title:
          'Unable to submit monthly statement. Please contact Finley support.',
        status: 'error',
        position: 'top',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifactContainerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifactContainerId },
      },
    ],
  });

  const { data, loading } = useGetStatementQuery({
    variables: {
      creditFacilityId,
      startDate,
      endDate,
    },
  });

  if (loading || !data?.generateStatement) {
    // @jendevelops TODO: add skeleton loader
    return <Skeleton height="100vh" w="100%" />;
  }

  // @jendevelops TODO: handle error state

  const {
    summary: { balances, totalBalance },
  } = data.generateStatement;

  return (
    <>
      <VStack
        backgroundColor={colors.white}
        w="100%"
        p="24px"
        borderRadius="8px"
        border={`1px solid ${colors.gray[100]}`}
      >
        <MonthlyStatementSummaryBalance
          totalBalance={totalBalance}
          balances={balances}
        />
      </VStack>

      <Flex w="100%" alignItems="center" justify="space-between">
        <Heading fontSize="20px">Daily Totals</Heading>

        {commonConsts.MSR_ALPHA_ORGANIZATION_SLUGS.includes(
          user?.organization.slug,
        ) &&
          artifactInstanceStatus !== 'expiredVersion' && (
            <AdjustCurrentLiabilitiesButton
              creditFacilityId={creditFacilityId}
              minDate={commonDate.fullDateStrToDate(startDate)}
              maxDate={commonDate.fullDateStrToDate(endDate)}
              setIsTableLoading={setIsTableLoading}
              buttonText="Adjust Current Liabilities"
            />
          )}
        {/* @jendevelops MSR-TODO Download Button */}
      </Flex>

      <MonthlyStatementDailyAggregateTable
        dailyAggregateData={data.generateStatement}
      />

      <DrawerFooter w="100%" position="sticky" bottom={0} bgColor="millet.50">
        <HStack spacing="32px" alignSelf="flex-end">
          <Button
            colorScheme="aurora"
            variant="outline"
            bgColor="white"
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            variant="solid"
            onClick={() => {
              const asOfTimestamp = commonDate.currentEpochMs();
              submitMonthlyStatement({
                variables: {
                  deliverableArtifactId: artifactId,
                  creditFacilityId,
                  reportingPeriodStartDate: startDate,
                  reportingPeriodEndDate: endDate,
                  asOfTimestamp,
                  deliverableArtifactInstanceId: artifactInstanceId,
                },
              });
            }}
            isLoading={isSubmitLoading}
          >
            Submit
          </Button>
        </HStack>
      </DrawerFooter>
    </>
  );
};

const MonthlyStatementsDrawerBody = ({
  artifact,
  creditFacilityId,
  onClose,
}: {
  artifact: DeliverableArtifactGQLType & {
    config: MonthlyStatementConfigGQLType;
  };
  creditFacilityId: string;
  onClose: () => void;
}) => {
  const [
    artifactInstance,
    setArtifactInstance,
  ] = useState<DeliverableArtifactInstanceGQLType | null>(
    artifact.instances[0] ?? null,
  );
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);

  useEffect(() => {
    // when artifactInstances is refetched, update the version to the latest
    if (artifact.instances.length > 0) {
      setArtifactInstance(artifact.instances[0]);
    }

    // refetch has finished
    setIsTableLoading(false);
  }, [artifact.instances]);

  const status = useMemo(() => {
    const currStatus = calculateArtifactInstanceStatus(artifactInstance);
    return currStatus;
  }, [artifactInstance]);

  const canOverwrite = artifactInstance?.active && status === 'submitted';

  const { startDate, endDate } = commonFormulas.evaluateStartAndEndDateFormulas(
    {
      startFormula: artifact.config.reportingPeriodStartDateFormula,
      endFormula: artifact.config.reportingPeriodEndDateFormula,

      variables: [
        {
          name: 'reportingDate',
          value: artifact.reportingDate,
          parseNumber: true,
        },
      ],
    },
  );

  const [
    createArtifactInstance,
    { loading: isLoading },
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  return (
    <VStack w="100%" spacing="40px" alignItems="flex-start">
      <HStack w="100%" spacing="32px">
        <Text>Version</Text>
        <InputGroup w="100px">
          <Select
            options={
              artifact.instances.length > 0
                ? artifact.instances.map((instance) => ({
                    label: `${instance.version}`,
                    value: instance,
                  }))
                : [
                    {
                      label: 1,
                      value: null,
                    },
                  ]
            }
            onChange={(newVersion) => {
              if (newVersion) {
                setArtifactInstance(newVersion);
              }
            }}
            value={artifactInstance}
          />
        </InputGroup>
        {canOverwrite && (
          <Button
            onClick={() => {
              createArtifactInstance({
                variables: {
                  deliverableArtifactId: artifact.id,
                  active: true,
                  status: 'inProgress',
                },
              });
              setIsTableLoading(true);
            }}
            isLoading={isLoading}
            variant="ghost"
            color="blue.400"
            leftIcon={
              <Icon icon="arrows-rotate" color="blue.400" boxSize={4} />
            }
          >
            Overwrite
          </Button>
        )}
      </HStack>
      <HStack w="100%" spacing="32px">
        <Text fontWeight="500">Status</Text>
        <ArtifactInstanceStatusBadge status={status} />
      </HStack>
      <Flex
        backgroundColor={colors.white}
        w="100%"
        p="24px 16px"
        borderRadius="8px"
        border={`1px solid ${colors.gray[100]}`}
      >
        <HStack w="50%" spacing="16px">
          <Text fontWeight="bold">Period</Text>
        </HStack>
        <Text fontWeight="500" flexGrow={1} ml="32px">
          {getHumanReadableDateRange({ startDate, endDate })}
        </Text>
      </Flex>
      {isTableLoading && <Skeleton height="50vh" w="100%" />}
      {(status === 'processing' || status === 'notYetSubmitted') &&
        !isTableLoading && (
          <MonthlyStatementInProgressDrawerBody
            artifactInstanceId={artifactInstance?.id}
            artifactInstanceStatus={status}
            artifactContainerId={artifact.containerId}
            artifactId={artifact.id}
            creditFacilityId={creditFacilityId}
            startDate={startDate}
            endDate={endDate}
            onClose={onClose}
            setIsTableLoading={setIsTableLoading}
          />
        )}
      {(status === 'submitted' || status === 'expiredVersion') &&
        artifactInstance &&
        !isTableLoading && (
          <MonthlyStatementSubmittedDrawerBody
            artifactInstance={artifactInstance}
          />
        )}
    </VStack>
  );
};

const DrawerBodyContents = ({
  creditFacilityName,
  creditFacilityId,
  organizationId,
  artifact,
  onClose,
  dataTape,
  onSubmitDataTape,
  errors,
}: Omit<DrawerViewProps, 'isOpen'>) => {
  switch (artifact.config.artifactType) {
    case 'monthlyStatement':
      return (
        <MonthlyStatementsDrawerBody
          artifact={
            artifact as DeliverableArtifactGQLType & {
              config: MonthlyStatementConfigGQLType;
            }
          }
          creditFacilityId={creditFacilityId}
          onClose={onClose}
        />
      );
    case 'financialStatement':
    case 'cashBalancesFeed':
    case 'supplementalTape':
    case 'loanTape':
    case 'repaymentTape':
    case 'financialCovenant':
    case 'portfolioCovenant':
    case 'orgProvidedInput':
    case 'monthlyServicerReportXlsx':
      return (
        <DefaultDrawerBody
          creditFacilityName={creditFacilityName}
          creditFacilityId={creditFacilityId}
          organizationId={organizationId}
          artifact={artifact}
          onClose={onClose}
          dataTape={dataTape}
          onSubmitDataTape={onSubmitDataTape}
          errors={errors}
        />
      );
    default:
      return finleyAssertUnreachable(artifact.config.artifactType);
  }
};

const DrawerView = ({
  creditFacilityName,
  creditFacilityId,
  organizationId,
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
  errors,
}: DrawerViewProps) => {
  return (
    <Drawer onClose={onClose} isOpen={isOpen} size="xl" closeOnOverlayClick>
      <DrawerOverlay bgColor="inherit" />
      <DrawerContent bgColor={colors.background[50]}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" mb={4}>{`${
          artifact.reportingDate
        } ${commonUtils.toTitleCase(
          artifact.config.artifactType,
        )}`}</DrawerHeader>
        <DrawerBody>
          <DrawerBodyContents
            creditFacilityName={creditFacilityName}
            creditFacilityId={creditFacilityId}
            organizationId={organizationId}
            artifact={artifact}
            onClose={onClose}
            dataTape={dataTape}
            onSubmitDataTape={onSubmitDataTape}
            errors={errors}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const InputTable = ({
  dependencies,
  deliverableArtifactToDataTapeMap,
  errorsMap,
  creditFacilityId,
  creditFacilityName,
  organizationId,
  openDrawerArtifact,
  getDataTape,
  uploadFile,
}: {
  dependencies: DeliverableArtifactGQLType[];
  deliverableArtifactToDataTapeMap: DeliverableArtifactToDataTapeMap;
  errorsMap: Record<
    string,
    {
      errors: progress.AggregatedError[];
      deliverableArtifactInstanceId: string;
    }
  >;
  creditFacilityId: string;
  creditFacilityName: string;
  organizationId: string;
  openDrawerArtifact: (dependency: DeliverableArtifactGQLType) => void;
  getDataTape: (
    artifactConfig: DeliverableArtifactConfigGQLType,
  ) => DataTapeState | undefined;
  uploadFile: ({
    file,
    tape,
    deliverableArtifactId,
    uploadReportingDate,
    fileVersion,
  }: {
    file: File;
    tape: DataTape;
    deliverableArtifactId: string;
    uploadReportingDate: Util.FullDateStr;
    fileVersion: number;
  }) => Promise<void>;
}) => {
  // tan stack sorting
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'calculatedStatus', desc: true },
    { id: 'reportingDate', desc: false },
    { id: 'type', desc: false },
  ]);

  // TAN STACK LOGIC
  const computedStatusSortOrder: { [key in ArtifactStatus]: number } = {
    notYetSubmitted: 3,
    processing: 2,
    submitted: 1,
  };

  const columns: ColumnDef<DeliverableArtifactGQLType, any>[] = [
    {
      accessorKey: 'calculatedStatus',
      accessorFn: (row) => calculateArtifactStatus(row),
      header: 'Status',
      enableSorting: true,
      enableColumnFilter: false,
      sortingFn: (rowA, rowB) => {
        const statusRowA = calculateArtifactStatus(rowA.original);
        const statusRowB = calculateArtifactStatus(rowB.original);

        if (
          computedStatusSortOrder[statusRowA] >
          computedStatusSortOrder[statusRowB]
        ) {
          return 1;
        }
        if (
          computedStatusSortOrder[statusRowA] <
          computedStatusSortOrder[statusRowB]
        ) {
          return -1;
        }
        return 0;
      },
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => {
        const dependency = row.original;

        const progressVal = deliverableArtifactToDataTapeMap[dependency.id];
        const errVal:
          | {
              errors: progress.AggregatedError[];
              deliverableArtifactInstanceId: string;
            }
          | undefined = errorsMap[dependency.id];
        return (
          <>
            {/* eslint-disable-next-line no-nested-ternary */}
            {errVal ? (
              <Tag
                colorScheme="red"
                variant="solid-muted"
                flexShrink={0}
                size="sm"
              >
                <TagLeftIcon>
                  <Icon icon="circle-x" />
                </TagLeftIcon>
                <TagLabel>Upload Error</TagLabel>
              </Tag>
            ) : progressVal?.status === 'uploading' ? (
              <UploadProgressBar progressValue={progressVal.loadingPercent} />
            ) : (
              <ArtifactStatusCell
                status={calculateArtifactStatus(dependency)}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'configKey',
      header: 'Name',
      enableSorting: false,
      enableColumnFilter: false,

      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
    },
    {
      accessorKey: 'type',
      accessorFn: (row) => commonUtils.toTitleCase(row.config.artifactType),
      enableSorting: true,
      enableColumnFilter: false,
      header: 'Type',
    },
    {
      id: 'reportingDate',
      accessorKey: 'reportingDate',
      enableSorting: true,
      enableColumnFilter: false,
      header: 'Reporting Date',
      cell: ({ getValue }) =>
        commonDate.fullDateStrToHumanDateString(getValue()),
    },
    {
      enableSorting: false,
      enableColumnFilter: false,
      header: 'Action',
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => {
        const dependency = row.original;

        const errVal:
          | {
              errors: progress.AggregatedError[];
              deliverableArtifactInstanceId: string;
            }
          | undefined = errorsMap[dependency.id];

        if (
          calculateArtifactStatus(dependency) === 'notYetSubmitted' ||
          errVal
        ) {
          switch (dependency.config.artifactType) {
            case 'monthlyStatement':
              return (
                <IconButton
                  variant="ghost"
                  icon={<Icon icon="file-magnifying-glass" color="blue.400" />}
                  aria-label="review monthly statement"
                  size="sm"
                  onClick={() => openDrawerArtifact(dependency)}
                />
              );
            default:
              return (
                <UploadButton
                  creditFacilityId={creditFacilityId}
                  creditFacilityName={creditFacilityName}
                  organizationId={organizationId}
                  artifact={dependency}
                  fontAwesomeIcon="arrow-up-from-bracket"
                  buttonText="Upload"
                  iconOnly
                  defaultToNewVersion={false}
                  dataTape={getDataTape(dependency.config)}
                  onSubmitDataTape={(
                    file,
                    tape,
                    uploadReportingDate,
                    fileVersion,
                  ) =>
                    uploadFile({
                      file,
                      tape,
                      deliverableArtifactId: dependency.id,
                      uploadReportingDate,
                      fileVersion,
                    })
                  }
                />
              );
          }
        }

        return (
          <Tooltip
            label={
              calculateArtifactStatus(dependency) === 'processing'
                ? 'Processing current upload.'
                : ''
            }
            aria-label="Upload tooltip"
          >
            <Text ml={3}>-</Text>
          </Tooltip>
        );
      },
    },
    {
      id: 'moreDetail',
      enableSorting: false,
      enableColumnFilter: false,
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => {
        const dependency = row.original;

        return (
          <IconButton
            aria-label="more details"
            size="sm"
            icon={<Icon icon="chevron-right" />}
            variant="ghost"
            _focus={{ boxShadow: 'none' }}
            onClick={() => {
              openDrawerArtifact(dependency);
            }}
          />
        );
      },
      meta: {
        style: {
          paddingRight: '20px',
          textAlign: 'right',
        },
      },
    },
  ];

  const table = useReactTable({
    data: dependencies,
    columns,
    enableMultiSort: true,
    enableColumnFilters: false,
    enableFilters: false,
    enableGlobalFilter: false,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box overflowY="auto" mt={8}>
      <Table borderWidth="1px" borderColor={colors.gray[100]}>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();

                return (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding ||
                      '8px 16px 8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={colors.black[95]}
                    width="auto"
                  >
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                        color={colors.aurora[400]}
                        fontWeight="thin"
                      >
                        {{
                          asc: <Icon icon="sort-up" />,
                          desc: <Icon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <Icon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody color="gray.600" fontSize="sm">
          {table.getRowModel().rows.map((row) => {
            const dependency = row.original;
            const errVal:
              | {
                  errors: progress.AggregatedError[];
                  deliverableArtifactInstanceId: string;
                }
              | undefined = errorsMap[dependency.id];

            return (
              <Fragment key={dependency.id}>
                <Tr
                  key={row.id}
                  borderBottomWidth={
                    !!errVal && errVal.errors.length > 0 ? 0 : '1px'
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      textAlign={cell.column.columnDef.meta?.style?.textAlign}
                      padding={cell.column.columnDef.meta?.style?.padding}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
                {!!errVal && errVal.errors.length > 0 && (
                  <Tr borderBottomWidth="1px">
                    <Td colSpan={7}>
                      <DataTapeErrorView errors={errVal.errors} numOfRows={5} />
                    </Td>
                  </Tr>
                )}
              </Fragment>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const params = useParams();
  const { deliverableId } = RouteParamsStruct.parse(params);

  const navigate = useNavigate();
  const toast = useToast();
  const client = useApolloClient();

  finleyAssert(deliverableId, 'deliverableId is required');

  const [stayOnPage, setStayOnPage] = useState(false);
  const [currentlyGeneratingReceipt, setCurrentlyGeneratingReceipt] = useState(
    false,
  );
  const {
    data: containerData,
    loading: containerLoading,
    error: containerError,
    refetch: refetchContainer,
  } = useGetDeliverableContainerByIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    pollInterval: currentlyGeneratingReceipt ? 5000 : 0,
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: { containerId: deliverableId },
  });
  useEffect(() => {
    if (containerData) {
      const container = containerData.deliverableContainer;
      setCurrentlyGeneratingReceipt(
        container.latestGenerateReceiptRecord?.status === 'initialized' ||
          container.latestGenerateReceiptRecord?.status === 'inProgress',
      );
    }
  }, [containerData]);

  const { data: receiptData } = useGetDeliverableContainerReceiptsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: { containerId: deliverableId },
  });

  const {
    data: artifactsData,
    loading: artifactsLoading,
    error: artifactsError,
    previousData: previousArtifactsData,
    refetch: refetchArtifacts,
  } = useGetDeliverableArtifactsByContainerIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: { containerId: deliverableId },
  });

  // maps bbpl artifact instance id to deliverable artifact id & instance id (for any inProgress/valid ones)
  const bbplInstanceToDeliverableArtifactMap = useMemo(() => {
    const mappedVals: Record<
      string,
      { artifactId: string; instanceId: string }
    > = {};

    if (artifactsData) {
      for (const deliverableArtifact of artifactsData.getDeliverableArtifactsByContainerId) {
        const deliverableActiveInstance = deliverableArtifact.instances.find(
          (i) => !!i.active,
        );
        if (
          deliverableActiveInstance &&
          deliverableActiveInstance.status === 'inProgress' &&
          deliverableActiveInstance.rawDatabase === 'pg' &&
          deliverableActiveInstance.rawNamespace ===
            'borrowing_base_pipeline_artifact_instances' &&
          deliverableActiveInstance.rawIdentifier
        ) {
          const bbplArtifactInstanceId = commonUtils.parseDbPgIdPointer(
            deliverableActiveInstance.rawIdentifier,
          );
          if (bbplArtifactInstanceId) {
            mappedVals[bbplArtifactInstanceId] = {
              artifactId: deliverableArtifact.id,
              instanceId: deliverableActiveInstance.id,
            };
          }
        }
      }
    }

    return mappedVals;
  }, [artifactsData]);

  const {
    data: errorsData,
  } = useGetBorrowingBasePipelineArtifactInstancesErrorsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    pollInterval: _.isEmpty(bbplInstanceToDeliverableArtifactMap) ? 0 : 60000, // don't poll if its empty, otherwise poll every minute
    variables: {
      rawArtifactInstanceIds: Object.keys(bbplInstanceToDeliverableArtifactMap),
    },
  });

  // Map from deliverable artifact id to bbpl errors.
  // An empty error array indicates an error is present, but we don't have specific details.
  const errorsMap = useMemo(() => {
    const errMap: Record<
      string,
      {
        errors: progress.AggregatedError[];
        deliverableArtifactInstanceId: string;
      }
    > = {};
    if (errorsData) {
      for (const err of errorsData.borrowingBasePipelineDataValidationErrors) {
        const deliverableArtifact =
          bbplInstanceToDeliverableArtifactMap[err.artifactInstanceId];
        if (deliverableArtifact) {
          const errors: progress.AggregatedError[] = err.progressBreakdown.flatMap(
            (e) => e.status.errors ?? [],
          );

          errMap[deliverableArtifact.artifactId] = {
            errors,
            deliverableArtifactInstanceId: deliverableArtifact.instanceId,
          };
        }
      }
    }
    return errMap;
  }, [bbplInstanceToDeliverableArtifactMap, errorsData]);

  const [
    refreshDeliverableMutation,
    { loading: refreshLoading },
  ] = useRefreshDeliverableMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: {
      deliverableContainerId: deliverableId,
    },
    onCompleted: () => {
      refetchContainer();
      refetchArtifacts();
    },
  });

  // executes refresh mutation on page load if any artifacts are in-progress
  useEffect(() => {
    // if `previousArtifactsData` is undefined we can guarantee that this is the first execution (on page load)
    if (previousArtifactsData === undefined && artifactsData) {
      const artifacts = artifactsData.getDeliverableArtifactsByContainerId;
      const dependencies = artifacts.filter((a) => a.inputType === 'input');

      const inputsNotComplete = _.some(
        dependencies,
        (d) =>
          calculateArtifactStatus(d) === 'processing' ||
          calculateArtifactStatus(d) === 'notYetSubmitted',
      );

      if (inputsNotComplete) {
        refreshDeliverableMutation();
      }
    }
  }, [
    artifactsData,
    deliverableId,
    previousArtifactsData,
    refreshDeliverableMutation,
  ]);

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  const [
    updateFailedDeliverableArtifactInstanceMutation,
  ] = useUpdateFailedDeliverableArtifactInstanceMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  const { data: requiredDataTapesData } = useGetRequiredDataTapesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      organizationId: user.organization.id,
      omitOrgProvidedInputGraphs: false,
    },
    fetchPolicy: 'network-only',
  });

  const [
    drawerArtifact,
    setDrawerArtifact,
  ] = useState<DeliverableArtifactGQLType>();

  // when artifact data updated, need to update data passed into the drawer
  useEffect(() => {
    if (drawerArtifact) {
      const updatedArtifact = artifactsData?.getDeliverableArtifactsByContainerId.find(
        (a) => a.id === drawerArtifact.id,
      );
      if (updatedArtifact) {
        setDrawerArtifact(updatedArtifact);
      }
    }
    // only want to update this on artifact data refetch, not when drawer is set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactsData]);

  const [openGenerateVersionModal, setOpenGenerateVersionModal] = useState(
    false,
  );
  // all input artifacts at the point of the modal being open
  const [
    generateOutputsModalFixedInputDependencies,
    setGenerateOutputsModalFixedInputDependencies,
  ] = useState<DeliverableArtifactGQLType[] | undefined>(undefined);

  // Track upload progression by deliverable artifact id
  const [
    deliverableArtifactToDataTapeMap,
    setDeliverableArtifactToDataTapeMap,
  ] = useState<DeliverableArtifactToDataTapeMap>({});

  // Slugs for data tapes for which uploads are in progress.
  const [inProgressSlugs, setInProgressSlugs] = useState<string[]>([]);

  // Slugs for uploads that have been completed.
  const [completedSlugs, setCompletedSlugs] = useState<string[]>([]);

  // If data tape upload is in progress: Display browser warning popup when user attempts to manually refresh or close page
  useEffect(() => {
    const alertCallback = (event: Event) => {
      // using all 3 methods of beforeunload activation to cover most browser cases
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = true;
      return '';
    };

    const isUploadInProgress = Object.values(
      deliverableArtifactToDataTapeMap,
    ).some((val) => val?.status === 'uploading');

    if (isUploadInProgress) {
      window.addEventListener('beforeunload', alertCallback);
    }

    // remove exit prompt event listener when unmounting
    return () => {
      window.removeEventListener('beforeunload', alertCallback);
    };
  }, [deliverableArtifactToDataTapeMap]);

  const [
    createDeliverableContainerReceiptMutation,
  ] = useCreateDeliverableContainerReceiptMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    onCompleted(data) {
      navigate(
        getDeliverableFinalOutputReceiptsViewUrl({
          containerId: deliverableId,
          receiptId: data.deliverableContainerReceipt,
        }),
      );
    },
  });

  // updates drawer
  const threadsQuery = useGetThreadsByDomainIdentifierQuery({
    variables: {
      domain: 'deliverables_container',
      domainIdentifier: deliverableId,
    },
  });

  const commentsDrawerDisclosure = useSearchParamsDisclosure({
    key: EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM,
  });

  if (threadsQuery.error) throw threadsQuery.error;
  if (
    !receiptData ||
    !requiredDataTapesData ||
    !containerData ||
    !containerData.deliverableContainer ||
    !artifactsData ||
    !artifactsData.getDeliverableArtifactsByContainerId ||
    !threadsQuery.data
  ) {
    return (
      <Skeleton height="600px" data-testid="deliverables-detail-view-loading" />
    );
  }

  if (containerError || artifactsError) {
    toast({ title: `Failed to load page.` });
    navigate('/deliverables');
  }

  const container = containerData.deliverableContainer;
  const artifacts = artifactsData.getDeliverableArtifactsByContainerId;
  const dependencies = artifacts.filter((a) => a.inputType === 'input');
  const finalArtifacts = artifacts.filter((a) => a.inputType === 'output');

  const title = `${user.organization.name} - ${commonUtils.toTitleCase(
    container.configName,
  )}`;

  const calculatedStatus = calculateDetailedStatus(container);

  /**
   * Everything below is to manage state for data tape uploads across modals, drawers, table views.
   * We cannot manage state across all these components when they are closed, so managing at the top level here.
   * 'uploadFile' puts the file into s3 and initiates polling for potential errors until files have reached the sanitized node.
   */

  // Create a map by loanProductSlugs -> dataTape for any that have them (see `loanProductSlugMap` below)
  // Create a map for all requiredDataTapes by bbplArtifactSlug (see `bbplArtifactSlugMap` below)
  const dataTapesWithLoanProductSlugs: (
    | RequiredDataTapeGQLType
    | RequiredXlsxTapeGQLType
  )[] = [];
  const requiredDataTapes = requiredDataTapesData.getRequiredDataTapes.requiredDataTapesGroupings.flatMap(
    (group) => [...group.requiredDataTapes, ...group.requiredXlsxTapes],
  );

  for (const tape of requiredDataTapes) {
    if (tape.loanProductSlug) {
      dataTapesWithLoanProductSlugs.push(tape);
    }
  }

  const loanProductSlugMap = _.groupBy(
    dataTapesWithLoanProductSlugs,
    (lp) => lp.loanProductSlug!, // we know that loanProductSlug will always exist due to for loop above.
  );

  const bbplArtifactSlugMap = _.groupBy(
    requiredDataTapes,
    (lp) => lp.artifactSlug,
  );

  const getDataTape = (
    artifactConfig: DeliverableArtifactConfigGQLType,
  ): DataTapeState | undefined => {
    let tapes:
      | (RequiredDataTapeGQLType | RequiredXlsxTapeGQLType)[]
      | undefined;
    let tape: RequiredDataTapeGQLType | RequiredXlsxTapeGQLType | undefined;

    switch (artifactConfig.__typename) {
      case 'FinancialStatementConfig':
      case 'SupplementalTapeConfig': {
        tapes = (bbplArtifactSlugMap[artifactConfig.artifactSlug] ?? []).filter(
          (t) => t.artifactType === 'rawSupplementalTape',
        );
        break;
      }
      case 'LoanTapeConfig': {
        tapes = (
          loanProductSlugMap[artifactConfig.loanProductSlug] ?? []
        ).filter((t) => t.artifactType === 'rawLoanTape');
        break;
      }
      case 'RepaymentTapeConfig': {
        tapes = (
          loanProductSlugMap[artifactConfig.loanProductSlug] ?? []
        ).filter((t) => t.artifactType === 'rawRepaymentTape');
        break;
      }
      case 'OrgProvidedInputConfig': {
        if (artifactConfig.bbplArtifactSlug) {
          tapes = (
            bbplArtifactSlugMap[artifactConfig.bbplArtifactSlug] ?? []
          ).filter((t) => t.artifactType === 'rawSupplementalTape');
        }
        break;
      }
      default:
        break;
    }

    if (!tapes) {
      // if wrong type or no tapes are found, return undefined (makes it impossible to upload)
      return undefined;
    }

    if (tapes && tapes.length > 0) {
      // find a tape with a valid credit facility id
      tape = tapes.find((t) =>
        t.validCreditFacilityIds.includes(container.creditFacilityId),
      );
    }

    // for supplemental nodes, `validCreditFacilityIds` will usually be an empty list
    if (
      !tape &&
      tapes.length === 1 &&
      tapes[0].validCreditFacilityIds.length === 0
    ) {
      tape = tapes[0];
    }

    if (tape) {
      const result = getDataTapeState({
        tape,
        inProgressSlugs,
        completedSlugs,
      });
      return result;
    }
    return undefined;
  };

  // Data tape upload file function
  const uploadFile = async ({
    file,
    tape,
    deliverableArtifactId,
    uploadReportingDate,
    fileVersion,
  }: {
    file: File;
    tape: DataTape;
    deliverableArtifactId: string;
    uploadReportingDate: Util.FullDateStr;
    fileVersion: number;
  }): Promise<void> => {
    const dataTapeArtifactSlug = tape.artifactSlug;

    if (inProgressSlugs.includes(dataTapeArtifactSlug)) return;

    // Reset error state and display loading icon instead of upload button
    setInProgressSlugs((prevState) => [...prevState, dataTapeArtifactSlug]);
    // Remove slug from completed
    setCompletedSlugs((prevState) =>
      prevState.filter((slug) => slug !== dataTapeArtifactSlug),
    );
    setDeliverableArtifactToDataTapeMap((prevState) => ({
      ...prevState,
      [deliverableArtifactId]: {
        loadingPercent: 0,
        status: 'none',
        bbplArtifactSlug: dataTapeArtifactSlug,
      },
    }));

    // TODO(jon): support error reporting for direct s3 upload.
    let status: DataTapeUiStatus = 'none';
    let loadingPercent: number = 0;
    try {
      const bbplInstanceId = await directUpload({
        client,
        file,
        organizationId: user.organization.id,
        artifactId: tape.artifactId,
        reportingDate: uploadReportingDate,
        fileVersion,
        onEventCallback: (loaded, total) => {
          const percentCompleted = Math.ceil((loaded * 100) / total);
          setDeliverableArtifactToDataTapeMap((prevState) => ({
            ...prevState,
            [deliverableArtifactId]: {
              loadingPercent: percentCompleted,
              status: 'uploading',
              bbplArtifactSlug: dataTapeArtifactSlug,
            },
          }));
        },
      });

      // if the previous upload returned a bbpl sanitization error,
      // then we want to mark the previous deliverables artifact instance id as FAILED
      // before creating a new 'inProgress' record for this upload
      const errorVal = errorsMap[deliverableArtifactId];
      if (errorVal) {
        await updateFailedDeliverableArtifactInstanceMutation({
          variables: {
            instanceId: errorVal.deliverableArtifactInstanceId,
          },
        });
      }

      await createDeliverableArtifactInstanceMutation({
        variables: {
          deliverableArtifactId,
          active: true,
          status: 'inProgress',
          rawDatabase: 'pg',
          rawNamespace: 'borrowing_base_pipeline_artifact_instances',
          rawIdentifier: commonUtils.generatePgIdentifier({
            column: 'id',
            id: bbplInstanceId,
          }),
        },
      });
      refetchArtifacts();
      refetchContainer();

      // remove in progress
      setInProgressSlugs((prevState) =>
        prevState.filter((slug) => slug !== dataTapeArtifactSlug),
      );

      // mark as completed
      setCompletedSlugs((prevState) => [...prevState, dataTapeArtifactSlug]);

      status = 'success';
      loadingPercent = 100;
    } catch (err: any) {
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing upload. Please try again, and contact Finley support if this persists.',
      });
      // remove in progress
      setInProgressSlugs((prevState) =>
        prevState.filter((slug) => slug !== dataTapeArtifactSlug),
      );

      // If somehow slug is in completed, removed from completed & mark as error here
      setCompletedSlugs((prevState) =>
        prevState.filter((slug) => slug !== dataTapeArtifactSlug),
      );

      status = 'error';
      loadingPercent = 0;
    } finally {
      setDeliverableArtifactToDataTapeMap((prevState) => ({
        ...prevState,
        [deliverableArtifactId]: {
          loadingPercent,
          status,
          bbplArtifactSlug: dataTapeArtifactSlug,
        },
      }));
    }
  };

  return (
    <Box height="100%">
      <Flex justify="space-between" flexDirection="column">
        <Text fontSize="xs" color="gray.800" letterSpacing="wide" pb="16px">
          <Link as={ReactRouterLink} to="/deliverables" fontWeight="semibold">
            Deliverables
          </Link>{' '}
          {`> ${title}`}
        </Text>
        {/* If we are currently generating a version alert here. */}
        {currentlyGeneratingReceipt && (
          <Alert
            status="warning"
            fontSize="small"
            fontWeight="bold"
            mb={4}
            borderRadius="6px"
          >
            <AlertIcon />
            Your report is being generated. You&apos;ll receive an email
            notification when it&apos;s ready.
          </Alert>
        )}

        {/* If a user stayed on the page while generating and it errored. */}
        {stayOnPage &&
          !currentlyGeneratingReceipt &&
          container.latestGenerateReceiptRecord?.status === 'error' && (
            <Alert
              status="error"
              fontSize="small"
              fontWeight="bold"
              mb={4}
              borderRadius="6px"
            >
              <AlertIcon />
              Failed to generate the report. An email with the error details has
              been sent.
            </Alert>
          )}

        {/* If a user stayed on the page while generating and it succeeded. */}
        {stayOnPage &&
          !currentlyGeneratingReceipt &&
          container.latestGenerateReceiptRecord?.status === 'success' && (
            <Alert
              status="success"
              fontSize="small"
              fontWeight="bold"
              mb={4}
              borderRadius="6px"
            >
              <AlertIcon />
              <HStack width="full">
                <Text>Your report was generated.</Text>
                <Spacer />
                <Button
                  variant="link"
                  fontSize="small"
                  fontWeight="bold"
                  color={colors.info}
                  onClick={() => {
                    const receiptId =
                      container.latestGenerateReceiptRecord
                        ?.successfulReceiptId;
                    if (!receiptId) {
                      toast({
                        ...errorToastConfig,
                        title:
                          'Deliverables: Missing successfulReceiptId for a successfully generated report.',
                      });
                    } else {
                      navigate(
                        getDeliverableFinalOutputReceiptsViewUrl({
                          containerId: deliverableId,
                          receiptId,
                        }),
                      );
                    }
                  }}
                >
                  View Report
                </Button>
              </HStack>
            </Alert>
          )}

        <HStack pb="16px" spacing={4}>
          <Heading as="h2" fontSize="3xl">
            {`${commonUtils.toTitleCase(
              container.configName,
            )} for ${commonDate.fullDateStrToShortMonthDayYearString(
              container.dueDate,
            )}`}
          </Heading>
          <Center>
            <Tooltip label="Refresh data" placement="top">
              <IconButton
                isLoading={
                  refreshLoading || containerLoading || artifactsLoading
                }
                aria-label="Refresh data"
                size="lg"
                icon={
                  <Icon icon="arrows-rotate" size="large" color="blue.500" />
                }
                variant="ghost"
                onClick={async () => {
                  await refreshDeliverableMutation();
                }}
              />
            </Tooltip>
          </Center>
          <DetailedStatusBadge status={calculatedStatus} />
          <Spacer />
          <DeliverableEventLogSingleEntityDrawer
            entityId={deliverableId}
            disclosure={commentsDrawerDisclosure}
            externalOrgOptions={container.capitalProviderEntities.map(
              (obj) => ({
                value: obj.id,
                label: obj.name,
              }),
            )}
          />

          {!currentlyGeneratingReceipt &&
            dependencies.every(
              (d) => calculateArtifactStatus(d) === 'submitted',
            ) && (
              <Button
                py={5}
                px={5}
                variant="outline"
                size="sm"
                whiteSpace="normal"
                borderColor={colors.aurora[400]}
                borderWidth="2px"
                color={colors.aurora[400]}
                _focus={{ boxShadow: 'none' }}
                isLoading={openGenerateVersionModal === true}
                isDisabled={
                  openGenerateVersionModal === true ||
                  dependencies.some(
                    (d) => calculateArtifactStatus(d) !== 'submitted',
                  )
                }
                onClick={async () => {
                  // there is a delay between clicking this button and the modal opening since we are fetching,
                  // so just setting a boolean here that should show a loading spinner and prevent users from double clicking
                  setOpenGenerateVersionModal(true);

                  await refreshDeliverableMutation();
                  const refetchedArtifacts = await refetchArtifacts();
                  const refetchedInputDependencies = refetchedArtifacts.data.getDeliverableArtifactsByContainerId.filter(
                    (a) => a.inputType === 'input',
                  );
                  refetchContainer();

                  if (finalArtifacts.length > 0) {
                    setGenerateOutputsModalFixedInputDependencies(
                      refetchedInputDependencies,
                    );
                  } else {
                    setGenerateOutputsModalFixedInputDependencies(undefined);
                    // create receipt mutation.. on success to re-direct to final outputs page for the version that was just generated
                    const dependenciesInstanceIds = getMostRecentActiveInputDependencyIds(
                      refetchedInputDependencies,
                    );
                    if (dependenciesInstanceIds) {
                      createDeliverableContainerReceiptMutation({
                        variables: {
                          containerId: deliverableId,
                          receipt: {
                            dependenciesInstanceIds,
                            finalArtifactsInstanceIds: [],
                          },
                          organizationId: user.organization.id,
                          creditFacilityId: container.creditFacilityId,
                          userId: user.id,
                        },
                      });
                    } else {
                      // this is an edge case, only happens if someone managed to successfully upload something (via manual uploader or something)
                      // in between when someone else is clicking this button in deliverables.
                      toast({
                        title:
                          'Not all required files have completed yet. Please wait for those to succeed before proceeding.',
                        status: 'warning',
                        position: 'top',
                        isClosable: true,
                      });
                    }
                  }
                }}
              >
                Generate Version
              </Button>
            )}
          {receiptData.deliverableContainerReceipts.length > 0 && (
            <Button
              py={5}
              px={6}
              ml={4}
              whiteSpace="normal"
              variant="solid"
              _focus={{ boxShadow: 'none' }}
              size="sm"
              colorScheme="aurora"
              color="white"
              isDisabled={receiptData.deliverableContainerReceipts.length === 0}
              onClick={() => {
                const latestReceipt =
                  receiptData.deliverableContainerReceipts[0];
                navigate(
                  getDeliverableFinalOutputReceiptsViewUrl({
                    containerId: deliverableId,
                    receiptId: latestReceipt.id,
                  }),
                );
              }}
            >
              View Versions
            </Button>
          )}
        </HStack>
        {generateOutputsModalFixedInputDependencies !== undefined && (
          <GenerateOutputsModal
            containerId={container.id}
            containerDueDate={container.dueDate}
            creditFacilityId={container.creditFacilityId}
            organizationId={user.organization.id}
            userId={user.id}
            finalArtifacts={finalArtifacts}
            fixedInputDependencies={generateOutputsModalFixedInputDependencies}
            isOpen
            onClose={() => {
              setGenerateOutputsModalFixedInputDependencies(undefined);
              refetchContainer();
              setOpenGenerateVersionModal(false);
            }}
            isGenerating={currentlyGeneratingReceipt}
            onGenerate={() => {
              setCurrentlyGeneratingReceipt(true);
              setStayOnPage(true);
            }}
          />
        )}
        <SimpleGrid columns={3} spacingX={10} spacingY="4px" mt={8} mb={6}>
          <Text fontSize="xs" color="gray.600">
            Credit Facility
          </Text>
          <Text fontSize="xs" color="gray.600">
            Deliverable Type
          </Text>
          <Text fontSize="xs" color="gray.600">
            Due Date
          </Text>

          <Text as="b" fontSize="sm">
            {container.creditFacilityName}
          </Text>
          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(container.type)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonDate.fullDateStrToHumanDateString(container.dueDate)}
          </Text>
        </SimpleGrid>
        <InputTable
          dependencies={dependencies}
          deliverableArtifactToDataTapeMap={deliverableArtifactToDataTapeMap}
          errorsMap={errorsMap}
          creditFacilityName={container.creditFacilityName}
          creditFacilityId={container.creditFacilityId}
          organizationId={user.organization.id}
          getDataTape={getDataTape}
          uploadFile={uploadFile}
          openDrawerArtifact={(artifact) => {
            setDrawerArtifact(artifact);
          }}
        />
        {drawerArtifact && (
          <DrawerView
            creditFacilityName={container.creditFacilityName}
            creditFacilityId={container.creditFacilityId}
            organizationId={user.organization.id}
            artifact={drawerArtifact}
            isOpen={!!drawerArtifact}
            onClose={() => setDrawerArtifact(undefined)}
            dataTape={getDataTape(drawerArtifact.config)}
            onSubmitDataTape={uploadFile}
            errors={errorsMap[drawerArtifact.id]}
          />
        )}
      </Flex>
    </Box>
  );
};

// Exports
export const Route = () => <RouteBase />;
